import { ReactElement, useState } from 'react';
import Header from './Header';
import {
  ActionData,
  DeviceAttributes,
  DeviceAttributesV1,
  Group,
  PriorityList
} from '../constants/telosair-interfaces';
import Button from '../controls/Button';
import { useData } from '../context/data.context';
import { getCSSClassBasedOnPriority } from '../_helpers/globalHelper';
import AddDeviceModal from '../modals/AddDevice';

type IndividualDeviceDetailsProps = {
  label: string;
  subValue?: string;
  value: string | number;
  report?: boolean;
  type?: 'tag' | 'default';
  tags?: string[];
  priority?: PriorityList;
};

/**
 * IndividualDeviceDetail represent the individual detail of the device.
 * @param props - Properties of the IndividualDeviceDetails component.
 * @returns - A React Component.
 */
function IndividualDeviceDetails(props: IndividualDeviceDetailsProps): ReactElement {
  const { label, value, report, subValue, type = 'default', tags, priority } = props;
  const filteredTags = tags?.filter((item) => item.length > 0);
  return (
    <div className="flex justify-between items-center text-gray-500 dark:text-gray-300 text-sm">
      <div className="py-3 font-medium flex">
        {label}{' '}
        {report ? <span className="material-icons text-error-600 text-sm ml-1">report</span> : ''}
      </div>
      {type === 'default' ? (
        <div
          className={`px-3 py-2 font-semibold capitalize ${
            priority && getCSSClassBasedOnPriority(priority, 'text')
          }`}
        >
          {value} {subValue && <span className="text-gray-500 text-xs">({subValue})</span>}
        </div>
      ) : (
        <div className="px-3 py-2">
          {filteredTags && filteredTags?.length > 0 ? (
            <div className="justify-start items-center gap-2 flex">
              {filteredTags?.slice(0, 2).map((tag) => (
                <div
                  key={tag}
                  className="px-2 py-0.5 dark:bg-gray-800 rounded justify-start items-center text-primary-100 text-xs"
                >
                  {tag}
                </div>
              ))}
              {filteredTags && filteredTags?.length > 2 && (
                <div
                  key="count"
                  className="px-2 py-0.5 dark:bg-gray-800 rounded justify-start items-center text-primary-100 text-xs"
                >
                  +{filteredTags.length - 2}
                </div>
              )}
            </div>
          ) : (
            <div className="font-semibold">-</div>
          )}
        </div>
      )}
    </div>
  );
}

type DeviceDetailsProps = {
  deviceData: DeviceAttributes[];
  groupDetails: Group | null;
  selectedDevice: DeviceAttributesV1 | null;
};

/**
 * A DeviceDetails Component represents the list of different-different state of devices.
 * @param props - Properties of the devideData Component.
 * @returns - DeviceDetails Component.
 */
export default function DeviceDetails(props: DeviceDetailsProps): ReactElement {
  const { deviceData, groupDetails, selectedDevice } = props;
  const { dataRatePerDevice, devices, fetchAllDevices } = useData();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionData, setActionData] = useState<ActionData<DeviceAttributes>>(
    {} as ActionData<DeviceAttributes>
  );
  const DEVICE_DETAILS = [
    {
      id: 1,
      deviceType: 'Devices',
      noOfDevice: groupDetails?.sensorDetailsList
        ? groupDetails.sensorDetailsList.length
        : deviceData.length
    },
    {
      id: 2,
      deviceType: 'Active',
      noOfDevice: '-'
    },
    {
      id: 3,
      deviceType: 'Inactive',
      noOfDevice: '-'
    },
    {
      id: 4,
      deviceType: 'Defective',
      noOfDevice: '-'
    },
    {
      id: 5,
      deviceType: 'Gateway',
      noOfDevice: '-'
    }
  ];

  /**
   * Function to handle Device Modal.
   * @param newDeviceAdded - To check weather a New device is added or not.
   */
  const handleDeviceModalClose = (newDeviceAdded: boolean): void => {
    if (newDeviceAdded) {
      fetchAllDevices();
    }
    setIsModalOpen(false);
    setActionData({} as ActionData<DeviceAttributes>);
  };
  if (selectedDevice?.serial_number) {
    const deviceDetails = devices.filter(
      (item) => item.serialNumber === selectedDevice.serial_number
    )[0];
    const dataRate = dataRatePerDevice && dataRatePerDevice[selectedDevice.serial_number];
    let priority = PriorityList.none;
    if (dataRate?.state === 'working') {
      priority = PriorityList.low;
    } else if (dataRate?.state === 'moderate') {
      priority = PriorityList.medium;
    } else if (dataRate?.state === 'default') {
      priority = PriorityList.high;
    }
    return (
      <div className="bg-white dark:bg-container-500 p-4 rounded w-full">
        <Header sectionName="Device Info" />
        <IndividualDeviceDetails
          label="Device name"
          value={`${deviceDetails?.deviceName} (#${selectedDevice.serial_number})`}
        />
        <IndividualDeviceDetails label="Device status" value={deviceDetails.status} />
        <IndividualDeviceDetails
          report={dataRate?.state === 'default'}
          label="Data rate"
          priority={priority}
          value={dataRate?.value ? `${parseFloat(dataRate?.value).toFixed(1)}%` : '-'}
        />
        <IndividualDeviceDetails
          label="Device type"
          value={deviceDetails?.type?.toString() || '-'}
        />
        <IndividualDeviceDetails label="Device Gateways" value="-" />
        <IndividualDeviceDetails
          type="tag"
          tags={deviceDetails?.tags || []}
          label="Tags"
          value="-"
        />
        {deviceDetails && (
          <div className="w-full flex justify-center items-center mt-6">
            <Button
              onClick={() => {
                setActionData({ data: deviceDetails, type: 'edit' });
                setIsModalOpen(true);
              }}
              size="md"
              theme="secondary-color"
              name="Edit Device"
            />
          </div>
        )}
        {isModalOpen && (
          <AddDeviceModal
            actionData={actionData}
            groups={[]}
            setIsGroupModalOpen={() => null}
            handleClose={handleDeviceModalClose}
          />
        )}
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-container-500 p-4 rounded w-full">
      <Header sectionName={groupDetails?.groupName || 'General Info'} />
      {DEVICE_DETAILS.map(({ id, deviceType, noOfDevice }) => (
        <IndividualDeviceDetails key={id} label={deviceType} value={noOfDevice} />
      ))}
    </div>
  );
}
